import * as React from "react";
import { getLabelForOrderMethodStatus } from "../../../../../helpers";
import { makeStyles } from "@mui/styles";
import {
  markOrderAsDtgCompletedMutation,
  markOrderAsDtgStartedMutation,
  markOrderAsNoLongerOnHoldMutation
} from "../../../../../graph";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import JobStatusIcon from "../../../../../components/JobStatusIcon";
import PlaceOrderOnHoldModal from "../../../../../components/PlaceOrderOnHoldModal";
import Typography from "@mui/material/Typography";
import withSnackbar from "../../../../../components/withSnackbar";

const useStyles = makeStyles(theme => ({
  buttonContainer: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: theme.spacing(1)
  },

  jobStatus: {
    marginLeft: theme.spacing(1)
  },

  spaceBetweenContainer: {
    display: "flex"
  },

  spacerRight: {
    marginRight: theme.spacing(1)
  },

  spinnerWrapper: {
    padding: theme.spacing(1),
    position: "relative",
    textAlign: "center"
  }
}));

const DTGOrderStatus = ({
  order: {
    canViewerCompleteDtg,
    canViewerPlaceOnHold,
    canViewerRemoveHold,
    canViewerStartDtg,
    directToGarmentStatus,
    id: orderId
  },
  showErrorSnackbar,
  showSuccessSnackbar
}) => {
  const classes = useStyles();

  // Mutations:
  const [markOrderAsDtgCompleted] = useMutation(
    markOrderAsDtgCompletedMutation
  );
  const [markOrderAsDtgStarted] = useMutation(markOrderAsDtgStartedMutation);
  const [markOrderAsNoLongerOnHold] = useMutation(
    markOrderAsNoLongerOnHoldMutation
  );

  // States:
  const [isMutatingOrder, setIsMutatingOrder] = useState(false);
  const [isPlacingOrderOnHold, setIsPlacingOrderOnHold] = useState(false);

  // Handlers:
  const handleHoldButtonClick = () => setIsPlacingOrderOnHold(true);

  const handlePlaceOrderOnHoldModalRequestClose = () =>
    setIsPlacingOrderOnHold(false);

  const handleRemoveHoldButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsNoLongerOnHold({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsNoLongerOnHold: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handleDTGCompletedButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsDtgCompleted({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsDtgCompleted: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handleStartButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsDtgStarted({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsDtgStarted: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  return (
    <div>
      <div className={classes.spaceBetweenContainer}>
        <JobStatusIcon status={directToGarmentStatus} />
        <div className={classes.jobStatus}>
          <Typography variant="h6">DTG</Typography>
          <Typography variant="body2">
            {getLabelForOrderMethodStatus(directToGarmentStatus)}
          </Typography>
        </div>
      </div>
      {isMutatingOrder ? (
        <div className={classes.spinnerWrapper}>
          <CircularProgress />
        </div>
      ) : (
        <div className={classes.buttonContainer}>
          {canViewerStartDtg && (
            <Button
              className={classes.spacerRight}
              color="primary"
              variant="contained"
              onClick={handleStartButtonClick}
            >
              Start
            </Button>
          )}
          {canViewerCompleteDtg && (
            <Button
              className={classes.spacerRight}
              color="primary"
              variant="contained"
              onClick={handleDTGCompletedButtonClick}
            >
              DTG Complete
            </Button>
          )}
          {canViewerPlaceOnHold && (
            <Button
              color="secondary"
              onClick={handleHoldButtonClick}
              variant="contained"
            >
              Hold
            </Button>
          )}
          {canViewerRemoveHold && (
            <Button
              color="secondary"
              onClick={handleRemoveHoldButtonClick}
              variant="contained"
            >
              Remove Hold
            </Button>
          )}
        </div>
      )}
      <PlaceOrderOnHoldModal
        isOpen={isPlacingOrderOnHold}
        onRequestClose={handlePlaceOrderOnHoldModalRequestClose}
        orderId={orderId}
      />
    </div>
  );
};

export default withSnackbar(DTGOrderStatus);
